import React, {useState, useEffect, useContext, useRef} from "react";
import {useHistory} from "react-router-dom";
import classNames from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Button,
} from "reactstrap";

import {
    Tooltip,
    OverlayTrigger
} from "react-bootstrap";

import ReactTable from "components/ReactTable/ReactTable.js";
import NotificationAlert from "react-notification-alert";
import SweetAlert from "react-bootstrap-sweetalert";
import rest from "../../API/api";
import { UserLoginContext } from "../../context/UserLoginContext";
import { authenticatedFetch } from "../../services/authService";

const AllWaiverSubmissions = () => {
    const [userState, setUserState] = useContext(UserLoginContext);
    const notificationAlertRef = useRef(null);
    const history = useHistory();
    const [dataLoaded, setDataLoaded] = useState(false);
    const [data, setData] = React.useState([]);
    const [allSubmissions, setAllSubmissions] = React.useState([]);
    const [alert, setalert] = useState(null);
    const [loading, setLoading] = useState(true);

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    const hideAlert = () => {
        setalert(null);
    };

    const deleteSubmission = async (submissionId) => {
        try {
            const response = await authenticatedFetch(`${rest.waiversubmissions}/${submissionId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete submission');
            }

            return true;
        } catch (error) {
            console.error('Error deleting submission:', error);
            throw error;
        }
    };

    const deleteSubmissionAlert = (submissionId, submissionName) => {
        setalert(
            <SweetAlert
                warning
                style={{ display: "block", marginTop: "-100px" }}
                title="Are you sure?"
                onConfirm={async () => {
                    try {
                        await deleteSubmission(submissionId);
                        hideAlert();
                        notify("success", "Waiver submission has been deleted successfully!");
                        // Refresh the submissions list
                        setDataLoaded(!dataLoaded);
                    } catch (error) {
                        hideAlert();
                        notify("danger", "Failed to delete waiver submission");
                    }
                }}
                onCancel={() => hideAlert()}
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                confirmBtnText="Yes, delete it!"
                cancelBtnText="Cancel"
                showCancel
            >
                You will not be able to recover the submission for {submissionName}!
            </SweetAlert>
        );
    };

    // Update table data whenever submissions change
    useEffect(() => {
        if (userState.status && allSubmissions) {
            const tableData = allSubmissions.map((submission, i) => {
                return {
                    id: submission._id,
                    Name: `${submission.FirstName} ${submission.LastName}`,
                    Email: submission.Email || "",
                    Phone: submission.Phone || "",
                    WaiverTitle: submission.WaiverTitle || "N/A",
                    Version: submission.WaiverVersion || "1.0",
                    DOB: submission.DOB ? new Date(submission.DOB).toLocaleDateString() : "",
                    SubmittedDate: submission.SignatureDate ? new Date(submission.SignatureDate).toLocaleDateString() : "",

                    actions: (
                        <div className="actions-right">
                            <OverlayTrigger placement="top"
                                            overlay={<Tooltip id={"view_" + i}>View Submission</Tooltip>}>
                                <span>
                                <Button
                                    onClick={async () => {
                                        let tURL = "/admin/viewallwaiversubmission/" + submission._id;
                                        await history.push(tURL);
                                    }}
                                    color="info"
                                    size="sm"
                                    className={classNames("btn-icon btn-link like",)}
                                >
                                    <i className="tim-icons icon-zoom-split"/>
                                </Button>{" "}
                                </span>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top"
                                            overlay={<Tooltip id={"delete_" + i}>Delete Submission</Tooltip>}>
                                <span>
                                <Button
                                    onClick={() => {
                                        deleteSubmissionAlert(submission._id, `${submission.FirstName} ${submission.LastName}`);
                                    }}
                                    color="danger"
                                    size="sm"
                                    className={classNames("btn-icon btn-link like",)}
                                >
                                    <i className="tim-icons icon-simple-remove"/>
                                </Button>{" "}
                                </span>
                            </OverlayTrigger>
                        </div>
                    )
                };
            });
            setData(tableData);
        }
    }, [allSubmissions, userState.status]);

    useEffect(() => {
        const getAllSubmissions = async () => {
            setLoading(true);
            try {
                // First, fetch all waivers
                const waiversResponse = await authenticatedFetch(rest.waivers, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                });
                const waivers = await waiversResponse.json();

                // Then, fetch submissions for each waiver
                const allSubs = [];
                for (const waiver of waivers) {
                    try {
                        const submissionsResponse = await authenticatedFetch(`${rest.waiversubmissions}/${waiver._id}`, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json;charset=UTF-8'
                            }
                        });
                        const submissions = await submissionsResponse.json();

                        // Add waiver title and version to each submission
                        if (Array.isArray(submissions)) {
                            submissions.forEach(submission => {
                                allSubs.push({
                                    ...submission,
                                    WaiverTitle: waiver.Title,
                                    WaiverVersion: waiver.Version || "1.0"
                                });
                            });
                        }
                    } catch (error) {
                        console.error(`Error fetching submissions for waiver ${waiver._id}:`, error);
                    }
                }

                setAllSubmissions(allSubs);
            } catch (error) {
                console.error('Error fetching waivers:', error);
                notify("danger", "Failed to load waiver submissions");
            } finally {
                setLoading(false);
            }
        };

        getAllSubmissions();
    }, [dataLoaded]);

    if (userState.status) {
        return (
            <>
                {alert}
                <div className="rna-container">
                    <NotificationAlert ref={notificationAlertRef} />
                </div>
                <div className="content">
                    <Row className="mb-4">
                        <Col md={8} className="ml-auto mr-auto">
                            <h2 className="text-center">All Waiver Submissions</h2>
                            <p className="text-center">
                                Search and view all waiver submissions across all waivers
                            </p>
                        </Col>
                    </Row>
                    <Row className="mt-5">
                        <Col xs={12} md={12}>
                            <Card>
                                <CardHeader>
                                    <CardTitle tag="h4">
                                        All Submissions ({allSubmissions.length})
                                        {loading && <span className="ml-2">(Loading...)</span>}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <ReactTable
                                        data={data}
                                        filterable
                                        columns={[
                                            {
                                                Header: "Name",
                                                accessor: "Name"
                                            },
                                            {
                                                Header: "Email",
                                                accessor: "Email"
                                            },
                                            {
                                                Header: "Phone",
                                                accessor: "Phone"
                                            },
                                            {
                                                Header: "Waiver",
                                                accessor: "WaiverTitle"
                                            },
                                            {
                                                Header: "Version",
                                                accessor: "Version",
                                                maxWidth: 100
                                            },
                                            {
                                                Header: "Date of Birth",
                                                accessor: "DOB"
                                            },
                                            {
                                                Header: "Submitted Date",
                                                accessor: "SubmittedDate"
                                            },
                                            {
                                                Header: "Actions",
                                                accessor: "actions",
                                                sortable: false,
                                                filterable: false
                                            }
                                        ]}
                                        defaultPageSize={10}
                                        showPaginationTop
                                        showPaginationBottom={false}
                                        className="-striped -highlight"
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </>

        );
    }

    else {
        history.push("/admin/dashboard");
        return(null);
    }
};

export default AllWaiverSubmissions;
